/**
 * ------ maintenance history ------
 * 01/07/2020 Ella Ma: when filter list is empty, filterEnable is false
 * 01/27/2021 Bowen Li: change the dialog to home component
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { ThreadFilterConfig, ThreadFilterEventType, OPEN_DIALOG } from './thread-filter.view-model';
import { AppState } from '../../redux';
import { StoreQuerierService } from '../../services/store-querier.service';
import { FilterPanelConfig, FilterPanelActions, FilterPanelEvents } from '../../widgets/filter-item/filter-panel/filter-panel.view-model';
import { FilterService } from '../../tamalelibs/services/filter.service';
import { ALL_VALUE, ANY_VALUE } from '../../tamalelibs/constants/business.constants';
import { ThreadFilterService } from './thread-filter.service';

@Component({
    selector: 'thread-filter-dialog',
    templateUrl: './thread-filter.component.html',
    styleUrls: ['./thread-filter.component.scss']
})
export class ThreadFilterComponent implements OnInit, OnDestroy {

    config: ThreadFilterConfig;
    filerPanelConfig: FilterPanelConfig = new FilterPanelConfig();

    iconName = 'filter';
    dialogOpened = false;
    filterSource: Array<any> = [];

    filterLogic: string = ALL_VALUE;
    filterLogicOptionDropdown: Array<any> = [ALL_VALUE, ANY_VALUE];

    // 10 items
    filterItems: Array<{ text: string, value: string }> = [
        { text: 'Note Type', value: 'NoteType' },
        { text: 'Entity Type', value: 'EntityType' },
        { text: 'Entities', value: 'Entities' },
        { text: 'Display Date', value: 'DisplayDate' },
        { text: 'Submitted Date', value: 'SubmittedDate' },

        { text: 'Source', value: 'Source' },
        { text: 'Submitter', value: 'Submitter' },
        { text: 'Priority', value: 'Priority' },
        { text: 'Sentiment', value: 'Sentiment' },
        { text: 'Subject', value: 'Subject' }
    ];

    private _destroySubscriptions: Array<Subscription> = [];
    private _dialogCloseDestroySubscriptions: Array<Subscription> = [];
    private _currentFilters: any[] = [];

    constructor(
        private _filterService: FilterService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _threadFilterService: ThreadFilterService,
    ) { }

    ngOnInit() {
        this._destroySubscriptions.push(
            this._threadFilterService.dialogOpen$.subscribe(action => this._onSubscription(action)),
        );
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach((item) => item.unsubscribe());
        this._dialogCloseDestroySubscriptions.forEach((item) => item.unsubscribe());
    }

    closeDialog() {
        this.dialogOpened = false;
    }

    onApplyClicked() {
        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.setFilterEnableAction,
            payload: this._currentFilters.length > 0
        });
        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.setFilterLogicAction,
            payload: this.filterLogic
        });

        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.setFilterItemsAction,
            payload: this._currentFilters
        });
        this.filerPanelConfig.filtersConfigList = JSON.parse(JSON.stringify(this._currentFilters));
        this.config.eventSubject$.next({
            type: ThreadFilterEventType.apply
        });

        this.closeDialog();
    }

    onCancelClicked() {
        this.closeDialog();
    }

    onSuspendClicked() {
        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.setFilterEnableAction,
            payload: false
        });

        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.setFilterItemsAction,
            payload: this._currentFilters
        });
        this.filerPanelConfig.filtersConfigList = JSON.parse(JSON.stringify(this._currentFilters));

        this.config.eventSubject$.next({
            type: ThreadFilterEventType.suspend
        });

        this.closeDialog();
    }
    /**
     * remove all data from store
     *
     * @memberof FiltersDialogComponent
     */
    onBtnReset() {
        this.filerPanelConfig.action$.next({
            type: FilterPanelActions.resetFilter,
            payload: []
        });
        this._currentFilters = [];
        this.filterLogic = ALL_VALUE;
    }

    private _filterPanelEventHandler(event) {
        if (event.type === FilterPanelEvents.change || event.type === FilterPanelEvents.delete) {
            this._currentFilters = event.payload;
        }
    }

    /**
     * binds click event on every item.
     *
     * @private
     * @memberof FiltersDialogComponent
     */
    private _initFilterTypes() {
        this.filterSource = [];
        // sets kendo dropdown button item.
        this.filterItems.forEach(element => {
            this.filterSource.push(
                {
                    textField: element.text,
                    valueField: element.value,
                    click: (dataItem) => {
                        this._filterValueChange(dataItem.textField, dataItem.valueField);
                    }
                }
            );
        });
    }

    /**
     * adds a new FiltersConfig instance into ngrx store per selected type.
     * Filter body UI binds on the store, so UI changes accordingly on data change.
     *
     * @private
     * @param {*} textField
     * @param {*} valueField
     * @memberof FiltersDialogComponent
     */
    private _filterValueChange(textField, valueField): void {
        const _filtersConfig = this._filterService.getNewFilterDefaultInstance(textField, valueField, false, false, true);
        this.filerPanelConfig.action$.next({
            type: FilterPanelActions.addFilter,
            payload: [_filtersConfig]
        });
    }

    private _onSubscription(action) {
        if (action.type === OPEN_DIALOG) {
            this.config = action.payload;
            this._initFilterTypes();
            this._initData();
            this._showDialog();
        }
    }

    private _initData() {
        if (this.config && this.config.filterStatusSelector) {
            this._dialogCloseDestroySubscriptions.push(
                this._store.pipe(
                    select(this.config.filterStatusSelector),
                    map(status => status ? 'filter' : 'filter-o'),
                ).subscribe(status => this.iconName = status)
            );
        }

        this.filerPanelConfig.filtersConfigList = this._storeQuerier.queryBySelector(this.config.filterItemsSelector);
        this.filterLogic = this._storeQuerier.queryBySelector(this.config.filterLogicSelector);

        this._dialogCloseDestroySubscriptions.push(
            this.filerPanelConfig.events$.subscribe(event => this._filterPanelEventHandler(event)),
        );
    }

    private _showDialog() {
        this.dialogOpened = true;
        this._currentFilters = this.filerPanelConfig.filtersConfigList;
        this.filterLogic = this._storeQuerier.queryBySelector(this.config.filterLogicSelector);
    }
}
